// Helpers shared by the daily / details / monthly reports.

export const MONTH_ORDER = {
  January: 1,
  February: 2,
  March: 3,
  April: 4,
  May: 5,
  June: 6,
  July: 7,
  August: 8,
  September: 9,
  October: 10,
  November: 11,
  December: 12,
};

const EN_MONTHS = Object.keys(MONTH_ORDER);

// Report dates are stored as "DD-MonthName-YYYY" (e.g. "19-January-2025").
// Parsing that with `new Date()` only works in Chrome's lenient parser, and
// `toLocaleString("default")` returned month names in the browser's locale, so
// both grouping and sorting broke outside an English Chrome. Parse explicitly.
export const parseReportDate = (value) => {
  const parts = String(value || "").split("-");
  if (parts.length === 3 && MONTH_ORDER[parts[1]]) {
    const day = Number(parts[0]);
    const year = Number(parts[2]);
    if (day && year) return { day, monthName: parts[1], year };
  }

  // Anything else (e.g. ISO strings) goes through Date, with English month
  // names so keys stay locale-independent.
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return {
    day: date.getDate(),
    monthName: EN_MONTHS[date.getMonth()],
    year: date.getFullYear(),
  };
};

// "MonthName-YYYY" group key, or "Unknown Date".
export const getMonthKey = (value) => {
  const parsed = parseReportDate(value);
  return parsed ? `${parsed.monthName}-${parsed.year}` : "Unknown Date";
};

// Month groups for display: newest first unless the user asked for oldest first; unknown keys stay last.
export const sortMonthEntries = (entries, ascending = false) =>
  [...entries].sort(([a], [b]) => {
    const known = (key) => Boolean(MONTH_ORDER[key.split("-")[0]]);
    if (known(a) !== known(b)) return known(a) ? -1 : 1;
    return ascending ? compareMonthKeys(a, b) : compareMonthKeys(b, a);
  });

// Sorts "MonthName-YYYY" keys chronologically (unknown keys last).
export const compareMonthKeys = (a, b) => {
  const [aMonth, aYear] = a.split("-");
  const [bMonth, bYear] = b.split("-");
  const aKnown = MONTH_ORDER[aMonth] && Number(aYear);
  const bKnown = MONTH_ORDER[bMonth] && Number(bYear);
  if (!aKnown || !bKnown) return aKnown ? -1 : bKnown ? 1 : 0;

  const yearDiff = Number(aYear) - Number(bYear);
  if (yearDiff !== 0) return yearDiff;
  return MONTH_ORDER[aMonth] - MONTH_ORDER[bMonth];
};

// Groups items by a key into a Map, preserving first-seen order.
export const groupBy = (items, getKey) => {
  const groups = new Map();
  items.forEach((item) => {
    const key = getKey(item);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(item);
  });
  return groups;
};

// When the store filter changes, drop selected branches that no longer exist
// among the remaining options. Otherwise they keep filtering the report while
// their tags render without a label, which looks like an empty result.
export const pruneSelection = (selected = [], available = []) => {
  if (!selected.length) return selected;
  const allowed = new Set(available);
  const next = selected.filter((value) => allowed.has(value));
  return next.length === selected.length ? selected : next;
};

// Lets a clickable summary row be toggled from the keyboard. Only reacts to
// keys pressed on the row itself, not on buttons nested inside it.
export const rowToggleProps = (expanded, onToggle) => ({
  role: "button",
  tabIndex: 0,
  "aria-expanded": expanded,
  onClick: onToggle,
  onKeyDown: (event) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onToggle();
    }
  },
});

// Stops a button click inside a clickable row from also toggling the row.
export const stopRowToggle = (handler) => (event) => {
  event.stopPropagation();
  handler(event);
};

// Store references (_id) of a group of report rows, for approval requests scoped
// by store identity rather than the store display name.
export const storeIdsOf = (items = []) => [
  ...new Set(items.map((item) => item?.storeId).filter(Boolean)),
];

// ---- Server-side group totals (Phase 4C-4) ----------------------------------------------------------------------------------------------
// The paged screens show one page of rows, but the headers above them (month, app, branch) must print the totals of the whole group. The
// backend's `summary?groupBy=...` answers those totals; these helpers match a header's rows to the groups.

const pad2 = (value) => String(value).padStart(2, "0");

// "YYYY-MM" of a report row, as the backend groups it: Daily / Details by `realDate` (UTC), Monthly by `year` + `month`; null when unknown.
export const rowMonthKey = (row, kind = "daily") => {
  if (!row) return null;
  if (kind === "monthly") return row.year && row.month ? `${row.year}-${pad2(row.month)}` : null;
  return row.realDate ? String(row.realDate).slice(0, 7) : null;
};

// "YYYY-MM-DD" of a Daily row (null when unknown).
export const rowDayKey = (row) => (row && row.realDate ? String(row.realDate).slice(0, 10) : null);

export const branchRefsOf = (items = []) => [...new Set(items.map((item) => (item && item.branchRef ? String(item.branchRef) : null)))];

// What a total covers. Apps and branches are counted from the group keys when the groups carry them; otherwise only a single group's own server count
// can be used (the counts of several groups overlap, so adding them would count a branch twice) and the number is left out.
const coverage = (hits, match) => {
  const whole = !("date" in match) && !("app" in match) && !match.branchRefs ? hits[0].monthCoverage : null;
  if (whole) return whole;
  const distinct = (key) => new Set(hits.map((group) => group[key]).filter(Boolean)).size;
  return {
    apps: "app" in hits[0] ? distinct("app") : hits.length === 1 ? hits[0].apps : null,
    branches: "branchRef" in hits[0] ? distinct("branchRef") : hits.length === 1 ? hits[0].branches : null,
  };
};

// Sums the groups that match every given key. Keys left undefined are not compared; `branchRefs` is a list. Returns null when nothing matches
// (the header then falls back to the rows it has), otherwise { count, pending, <field>: sum }.
export function groupTotals(groups, match, fields = []) {
  if (!Array.isArray(groups)) return null;
  const same = (a, b) => (a ?? null) === (b ?? null);
  const hits = groups.filter((group) => {
    if ("month" in match && !same(group.month, match.month)) return false;
    if ("date" in match && !same(group.date, match.date)) return false;
    if ("app" in match && !same(group.app, match.app)) return false;
    if (match.branchRefs && !match.branchRefs.some((ref) => same(group.branchRef, ref))) return false;
    return true;
  });
  if (!hits.length) return null;
  const totals = { count: 0, pending: 0, notes: [], ...coverage(hits, match) };
  fields.forEach((field) => { totals[field] = 0; });
  hits.forEach((group) => {
    totals.count += group.count || 0;
    totals.pending += group.pending || 0;
    fields.forEach((field) => { totals[field] += Number(group[field]) || 0; });
    (group.notes || []).forEach((note) => { if (!totals.notes.includes(note)) totals.notes.push(note); });
  });
  return totals;
}

// Month totals with the open month's detail in place of its single total, so nothing is counted twice. The month's own server counts travel along.
export const mergeMonthDetail = (monthGroups, month, detailGroups) => {
  const detail = month && detailGroups ? detailGroups.filter((group) => group.month === month) : [];
  if (!detail.length) return monthGroups;
  const own = monthGroups.find((group) => group.month === month);
  const monthCoverage = own ? { apps: own.apps, branches: own.branches } : null;
  return [...monthGroups.filter((group) => group.month !== month), ...detail.map((group) => ({ ...group, monthCoverage }))];
};

const EN_MONTH_OF = (month) => EN_MONTHS[Number(month) - 1];

// Rows that carry a hierarchy level the browser has no real rows for: one per server group, with the fields the expand components group by and their
// approval actions read (store, date, app, branch). They are never shown as records and never edited or deleted; their amounts come from the groups.
export const anchorRows = (kind, groups, storeName) => groups.filter((group) => group.month).map((group) => {
  const [year, month] = group.month.split("-");
  const day = group.date ? group.date.slice(8, 10) : "01";
  return {
    storeId: group.storeId,
    storeName,
    app: group.app,
    branchRef: group.branchRef,
    branchName: group.branchName,
    year: Number(year),
    month: Number(month),
    date: kind === "monthly" ? `${EN_MONTH_OF(month)}-${year}` : `${day}-${EN_MONTH_OF(month)}-${year}`,
    realDate: `${year}-${month}-${day}T00:00:00.000Z`,
    isApproved: !(group.pending > 0),
  };
});

// The "has a row awaiting approval" marker the headers test with `find(a => a.isApproved === false)`.
export const pendingMarker = (totals, fallback) => (totals ? (totals.pending > 0 ? { isApproved: false } : undefined) : fallback);
